// LSA1 stores filename and media type inside authenticated ciphertext. The key never leaves this browser.
const encoder = { encode: (value) => new TextEncoder().encode(value) };
const decoder = { decode: (value) => new TextDecoder().decode(value) };
export function keyText(bytes) { return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""); }
export function readKey(value) {
  const normalized = value.trim().replace(/-/g, "+").replace(/_/g, "/");
  let result;
  try { result = Uint8Array.from(atob(normalized), (char) => char.charCodeAt(0)); } catch (_) { throw new Error("Clé de déchiffrement invalide."); }
  if (result.length !== 32) throw new Error("La clé doit contenir 32 octets.");
  return result;
}
export async function encryptFile(file, policy) {
  const raw = crypto.getRandomValues(new Uint8Array(32));
  const key = await crypto.subtle.importKey("raw", raw, "AES-GCM", false, ["encrypt"]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const metadata = encoder.encode(JSON.stringify({ name: file.name, type: file.type }));
  const length = new Uint8Array(4); new DataView(length.buffer).setUint32(0, metadata.byteLength);
  const plain = await new Blob([length, metadata, file]).arrayBuffer();
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: encoder.encode("LSA1") }, key, plain);
  const encrypted = { blob: new Blob([encoder.encode("LSA1"), iv, ciphertext], { type: "application/octet-stream" }), key: keyText(raw) };
  if (policy?.public_key_pem) {
    const publicKey = await crypto.subtle.importKey("spki", pemBytes(policy.public_key_pem, "PUBLIC KEY"), { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"]);
    const wrapped = new Uint8Array(await crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, raw));
    encrypted.envelope = { algorithm: "RSA-OAEP-SHA256", key_id: policy.key_id, wrapped_key: btoa(String.fromCharCode(...wrapped)) };
  } else if (policy?.required) throw new Error("La clé publique de l’organisation est indisponible.");
  return encrypted;
}

function pemBytes(pem, label) {
  const value = pem.replace(`-----BEGIN ${label}-----`, "").replace(`-----END ${label}-----`, "").replace(/\s/g, "");
  return Uint8Array.from(atob(value), (char) => char.charCodeAt(0));
}

export async function unwrapFileKey(secret, envelope) {
  if (!secret.includes("-----BEGIN PRIVATE KEY-----")) return secret;
  if (!envelope || envelope.algorithm !== "RSA-OAEP-SHA256") throw new Error("Enveloppe de clé client indisponible.");
  const key = await crypto.subtle.importKey("pkcs8", pemBytes(secret, "PRIVATE KEY"), { name: "RSA-OAEP", hash: "SHA-256" }, false, ["decrypt"]);
  const raw = await crypto.subtle.decrypt({ name: "RSA-OAEP" }, key, Uint8Array.from(atob(envelope.wrapped_key), (char) => char.charCodeAt(0)));
  return keyText(new Uint8Array(raw));
}
export async function decryptFile(blob, secret) {
  const raw = new Uint8Array(await blob.arrayBuffer());
  if (raw.byteLength < 32 || decoder.decode(raw.slice(0, 4)) !== "LSA1") throw new Error("Format de fichier chiffré inconnu.");
  const key = await crypto.subtle.importKey("raw", readKey(secret), "AES-GCM", false, ["decrypt"]);
  let plain;
  try { plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: raw.slice(4, 16), additionalData: encoder.encode("LSA1") }, key, raw.slice(16)); }
  catch (_) { throw new Error("Clé incorrecte ou fichier endommagé."); }
  const bytes = new Uint8Array(plain);
  const size = new DataView(plain).getUint32(0);
  if (size > 16384 || size > bytes.byteLength - 4) throw new Error("Métadonnées chiffrées invalides.");
  const metadata = JSON.parse(decoder.decode(bytes.slice(4, 4 + size)));
  const name = String(metadata.name || "fichier").replace(/[\\/\u0000-\u001f]/g, "_");
  return { blob: new Blob([bytes.slice(4 + size)], { type: "application/octet-stream" }), name };
}
export function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob), link = document.createElement("a");
  link.href = url; link.download = name; link.click(); setTimeout(() => URL.revokeObjectURL(url), 60000);
}
export async function storeEncrypted(api, endpoint, encrypted) {
  const digest = await crypto.subtle.digest("SHA-256", await encrypted.blob.arrayBuffer());
  const ciphertext_sha256 = Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("");
  const { data } = await api.post(endpoint, { size: encrypted.blob.size, encryption: "AES-256-GCM-LSA1", ciphertext_sha256, ...(encrypted.envelope ? { key_envelope: encrypted.envelope } : {}) });
  if (data.upload) {
    const form = new FormData();
    Object.entries(data.upload.fields).forEach(([key, value]) => form.append(key, value)); form.append("file", encrypted.blob);
    const result = await fetch(data.upload.url, { method: "POST", body: form, credentials: "omit", referrerPolicy: "no-referrer" });
    if (!result.ok) throw new Error("Le transfert chiffré a échoué. Conservez votre clé et réessayez.");
  } else if (!["ready", "finalizing"].includes(data.state)) throw new Error("Réservation de transfert invalide.");
  await api.post(`/files/${data.id}/complete`);
  return data.id;
}
